/**
 * @file SidebarToggle.tsx
 * @description Mobile sidebar toggle button - small handle visible on left edge
 * Shows on mobile to open/close the sidebar with smooth slide animation
 */

'use client';

import { useEffect, useState } from 'react';

interface SidebarToggleProps {
  isOpen: boolean;
  onToggle: () => void;
}

export default function SidebarToggle({ isOpen, onToggle }: SidebarToggleProps) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Don't render on desktop
  if (!isMobile) return null;

  return (
    <>
      <button
        className={`sidebar-toggle ${isOpen ? 'open' : ''}`}
        onClick={onToggle}
        aria-label={isOpen ? 'Zamknij sidebar' : 'Otwórz sidebar'}
        aria-expanded={isOpen}
      >
        <span className="sidebar-toggle-icon">
          {isOpen ? '←' : '→'}
        </span>
      </button>

      <style jsx>{`
        .sidebar-toggle {
          position: fixed;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          z-index: 1000;

          /* Appearance - Green theme */
          background: rgba(255, 255, 255, 0.98);
          border: none;
          border-right: 2px solid rgba(16, 185, 129, 0.5);
          border-top-right-radius: 8px;
          border-bottom-right-radius: 8px;

          /* Size */
          width: 30px;
          height: 100px;
          padding: 0;

          /* Layout */
          display: flex;
          align-items: center;
          justify-content: center;

          /* Interaction */
          cursor: pointer;
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;

          /* Animation */
          transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);

          /* Shadow */
          box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
        }

        .sidebar-toggle:hover {
          background: rgba(241, 248, 244, 0.98);
          border-right-color: rgba(16, 185, 129, 0.8);
          width: 35px;
        }

        .sidebar-toggle:active {
          background: rgba(248, 250, 249, 0.98);
          transform: translateY(-50%) scale(0.95);
        }

        .sidebar-toggle.open {
          left: calc(85vw - 30px);
          max-width: calc(400px - 30px);
          background: rgba(220, 38, 38, 0.9);
          border-right-color: rgba(239, 68, 68, 0.8);
          border-top-left-radius: 8px;
          border-bottom-left-radius: 8px;
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          border-left: 2px solid rgba(239, 68, 68, 0.8);
          border-right: none;
        }

        .sidebar-toggle.open:hover {
          background: rgba(185, 28, 28, 0.95);
          width: 35px;
        }

        .sidebar-toggle-icon {
          font-size: 20px;
          color: #059669;
          font-weight: bold;
          line-height: 1;
          user-select: none;

          /* Animation */
          transition: transform 200ms ease;
        }

        .sidebar-toggle.open .sidebar-toggle-icon {
          color: #ffffff;
        }

        .sidebar-toggle:hover .sidebar-toggle-icon {
          transform: scale(1.2);
        }

        /* Accessibility */
        .sidebar-toggle:focus {
          outline: 2px solid rgba(16, 185, 129, 0.8);
          outline-offset: 2px;
        }

        .sidebar-toggle:focus:not(:focus-visible) {
          outline: none;
        }

        /* Tablet size - hide toggle */
        @media (min-width: 768px) {
          .sidebar-toggle {
            display: none;
          }
        }

        /* Small phones */
        @media (max-width: 375px) {
          .sidebar-toggle {
            width: 25px;
            height: 80px;
          }

          .sidebar-toggle-icon {
            font-size: 18px;
          }
        }
      `}</style>
    </>
  );
}
